import { TypedDocumentNode } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiCallbacks, ApiResult, ApiState, callApiMutation } from 'rootSaga';
import { getDispatch } from 'store';
import { Mutation } from '../types/schema';

interface UseMutationSagaOptions<TData, TVariables> extends ApiCallbacks<TData> {
  variables?: TVariables;
  authentication?: boolean;
}

type MutateFunction<TData, TVariables> = (options?: UseMutationSagaOptions<TData, TVariables>) => Promise<ApiResult<TData>>;

export type GMutation<K extends keyof Mutation> = Mutation[K];

/**
 * Dispatches a mutation action into the saga and manages local loading/data/error state.
 * Multiple parallel mutations are supported — each call is independent.
 *
 * @example
 * const [addFav, { loading }] = useMutationSaga(addFavorite);
 * addFav({ variables: { id: product.id }, onSuccess: () => message.success('Added!') });
 */
function useMutationSaga<TData = any, TVariables = any>(
  gqlQueryMutate: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: UseMutationSagaOptions<TData, TVariables>,
): [MutateFunction<TData, TVariables>, ApiState<TData>] {
  const dispatch = getDispatch();
  const isMounted = useRef(true);

  const [state, setState] = useState<ApiState<TData>>({
    data: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const mutate = useCallback<MutateFunction<TData, TVariables>>(
    (mutateOptions) => {
      return new Promise<ApiResult<TData>>((resolve) => {
        setState((s) => ({ ...s, loading: true, error: null }));
        dispatch(
          callApiMutation<TData, TVariables>({
            gqlQueryMutate,
            variables: mutateOptions?.variables ? mutateOptions?.variables : options?.variables || ({} as TVariables),
            authentication:
              mutateOptions && Object.hasOwn(mutateOptions, 'authentication')
                ? mutateOptions.authentication
                : options && Object.hasOwn(options, 'authentication')
                  ? options?.authentication
                  : true,
            onSuccess: (data: TData) => {
              if (isMounted.current) {
                setState({ data, loading: false, error: null });
                const onSuccess = mutateOptions?.onSuccess ?? options?.onSuccess;
                onSuccess?.(data);
              }
              resolve(data);
            },
            onError: (error: any) => {
              if (isMounted.current) {
                setState((s) => ({ ...s, loading: false, error }));
                const onError = mutateOptions?.onError ?? options?.onError;
                onError?.(error);
              }
              resolve(error);
            },
          }),
        );
      });
    },
    [gqlQueryMutate, options],
  );

  return [mutate, state];
}

export default useMutationSaga;
